import api from './api';
import { uploadFile } from './uploads';
import type {
  Review,
  CreateReviewRequest,
  UpdateReviewRequest,
  HelpfulVoteResult,
  PaginatedResult,
} from '../types';

export type { PaginatedResult };

export const reviewsService = {
  async getById(id: string): Promise<Review> {
    const response = await api.get<Review>(`/reviews/${id}`);
    return response.data;
  },

  async getByProductId(productId: string, page = 1, pageSize = 10, sort = 'newest'): Promise<PaginatedResult<Review>> {
    const response = await api.get<PaginatedResult<Review>>(`/reviews/product/${productId}`, {
      params: { page, pageSize, sort },
    });
    return response.data;
  },

  async create(data: CreateReviewRequest): Promise<Review> {
    const response = await api.post<Review>('/reviews', data);
    return response.data;
  },

  async update(id: string, data: UpdateReviewRequest): Promise<Review> {
    const response = await api.put<Review>(`/reviews/${id}`, data);
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/reviews/${id}`);
  },

  async voteHelpful(id: string, isHelpful: boolean): Promise<HelpfulVoteResult> {
    const response = await api.post<HelpfulVoteResult>(`/reviews/${id}/vote-helpful`, { isHelpful });
    return response.data;
  },

  async uploadImages(id: string, files: File[]): Promise<Review> {
    // Upload files directly to Supabase Storage, then save URLs to our API
    const imageUrls = await Promise.all(
      files.map((file) => uploadFile(file, `reviews/${id}`))
    );
    const response = await api.post<Review>(`/reviews/${id}/images`, { imageUrls });
    return response.data;
  },

  async removeImage(id: string, imageUrl: string): Promise<void> {
    await api.delete(`/reviews/${id}/images`, { data: { imageUrl } });
  },

  async save(id: string): Promise<void> {
    await api.post(`/reviews/${id}/save`);
  },

  async unsave(id: string): Promise<void> {
    await api.delete(`/reviews/${id}/save`);
  },

  async getSaved(page = 1, pageSize = 10): Promise<PaginatedResult<Review>> {
    const response = await api.get<PaginatedResult<Review>>('/reviews/saved', {
      params: { page, pageSize },
    });
    return response.data;
  },

  async getSimilar(reviewId: string, limit = 4): Promise<Review[]> {
    const response = await api.get<Review[]>(`/reviews/${reviewId}/similar`, { params: { limit } });
    return response.data;
  },

  async getByUserIdPaginated(userId: string, page = 1, pageSize = 4, excludeId?: string): Promise<{ items: Review[]; totalCount: number }> {
    const response = await api.get<{ items: Review[]; totalCount: number }>(`/reviews/user/${userId}`, {
      params: { page, pageSize, ...(excludeId ? { exclude: excludeId } : {}) },
    });
    return response.data;
  },

  async getByCategory(category: string, page = 1, pageSize = 20, sort = 'newest', minRating?: number): Promise<PaginatedResult<Review>> {
    const response = await api.get<PaginatedResult<Review>>(`/reviews/category/${encodeURIComponent(category)}`, {
      params: { page, pageSize, sort, ...(minRating ? { minRating } : {}) },
    });
    return response.data;
  },
};
